import React, {useCallback, useEffect, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {Button, Space, Tabs, message} from 'antd';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {useDspOptions} from '../../../hooks/useDspOptions';
import {showError} from '../../../utils/exceptionUtils';
import PodRiskReviewDetailDrawer from './components/PodRiskReviewDetailDrawer';
import PodRiskReviewFilter from './components/PodRiskReviewFilter';
import PodRiskReviewTable from './components/PodRiskReviewTable';
import {
    DOWNLOAD_POD_RISK_REVIEW,
    DownloadPodRiskReviewResponse
} from './graphql/mutations/podRiskReviewMutations';
import {
    GET_POD_RISK_REVIEW_COUNT,
    GetPodRiskReviewCountResponse
} from './graphql/queries/getPodRiskReviewCount';
import {
    POD_RISK_REVIEW_ACTION_ENABLED,
    POD_RISK_REVIEW_DOWNLOAD_ENABLED,
    PodRiskReviewFilters,
    PodRiskReviewTabType,
    PodRiskReviewType
} from './graphql/types/PodRiskReviewType';
import {usePodRiskTypeOptions} from './hooks/usePodRiskTypeOptions';
import {
    buildFiltersInputFromFilters,
    usePodRiskReviewQuery
} from './hooks/usePodRiskReviewQuery';

/**
 * 默认筛选：创建时间必填，默认最近一个月。
 * 必填是性能兜底 —— 列表主查询按 created_at 排序，无时间条件的全量扫描在数据量上来后很危险。
 */
const getDefaultFilters = (): PodRiskReviewFilters => ({
    createdAt: [dayjs().subtract(1, 'month').startOf('day'), dayjs().endOf('day')]
});

/**
 * 舆情 POD 审核工作台
 *
 * 审核单由风控命中后自动建单，本页不提供手工建单入口。
 * Tab 与「审核状态」筛选以 AND 组合，二者矛盾时（如待审核 + 不存在风险）返回空结果。
 * 批量审核为纯前端交互：Drawer 内逐条审、审完跳下一条，服务端只提供单条接口。
 */
const PodRiskReviewPage: React.FC = () => {
    const {t, i18n} = useTranslation();

    const [activeTab, setActiveTab] = useState<PodRiskReviewTabType>(PodRiskReviewTabType.NEW);
    const [tempFilters, setTempFilters] = useState<PodRiskReviewFilters>(getDefaultFilters());
    const [filters, setFilters] = useState<PodRiskReviewFilters>(getDefaultFilters());
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<PodRiskReviewType[]>([]);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [reviewIds, setReviewIds] = useState<string[]>([]);

    const {podRiskReviewData, loading, executeQuery, totalElements} = usePodRiskReviewQuery();
    const {riskTypeOptions, riskTypeTextByCode, loading: riskTypesLoading} =
        usePodRiskTypeOptions();
    const {dspOptions, loading: dspLoading} = useDspOptions();
    const [downloadPodRiskReview, {loading: downloadLoading}] =
        useMutation<DownloadPodRiskReviewResponse>(DOWNLOAD_POD_RISK_REVIEW);

    // 配送商名称后端不回查，列表与详情都靠这份 id→name 映射
    const dspNameById: Record<string, string> = {};
    (dspOptions || []).forEach(dsp => {
        if (dsp.value) {
            dspNameById[String(dsp.value)] = dsp.label;
        }
    });
    const dspSelectOptions = (dspOptions || []).map(dsp => ({
        value: String(dsp.value),
        label: dsp.label
    }));

    // 只为非当前 Tab 发计数请求：当前 Tab 的计数与列表 totalElements 是同一个数，
    // 复用它可省掉首屏 1 次请求。用已提交的 filters 而非 tempFilters，与列表保持同源。
    const inactiveTab =
        activeTab === PodRiskReviewTabType.NEW
            ? PodRiskReviewTabType.TERMINATED
            : PodRiskReviewTabType.NEW;
    const {
        data: inactiveCountData,
        loading: inactiveCountLoading,
        refetch: refetchInactiveCount
    } = useQuery<GetPodRiskReviewCountResponse>(GET_POD_RISK_REVIEW_COUNT, {
        variables: {filters: buildFiltersInputFromFilters(filters, inactiveTab)},
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only'
    });

    const refetchCounts = useCallback(() => {
        void refetchInactiveCount();
    }, [refetchInactiveCount]);

    const clearSelection = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };

    const reload = useCallback(
        (
            nextFilters: PodRiskReviewFilters,
            page: number,
            size: number,
            tab: PodRiskReviewTabType
        ) => {
            executeQuery(nextFilters, page, size, tab);
            clearSelection();
        },
        [executeQuery]
    );

    // 首屏与 Tab 切换时回到第一页重查
    useEffect(() => {
        setCurrentPage(1);
        reload(filters, 1, pageSize, activeTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleSearch = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
        reload(tempFilters, 1, pageSize, activeTab);
    };

    const handleReset = () => {
        const defaults = getDefaultFilters();
        setTempFilters(defaults);
        setFilters(defaults);
        setCurrentPage(1);
        reload(defaults, 1, pageSize, activeTab);
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
        reload(filters, page, size, activeTab);
    };

    const showDetail = (record: PodRiskReviewType) => {
        setReviewIds([record.reviewTaskId]);
        setDrawerVisible(true);
    };

    // 导出当前 Tab + 当前已提交筛选条件的全量数据；文件由服务端异步生成，用户到下载中心取
    const handleDownload = async () => {
        try {
            const {data} = await downloadPodRiskReview({
                variables: {
                    filters: buildFiltersInputFromFilters(filters, activeTab),
                    // i18n 语言可能是 zh-CN 这类带地区的格式，后端只认前两位
                    language: (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0],
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                }
            });
            const result = data?.downloadPodRiskReview;
            if (result?.success) {
                message.success(t('podRiskReview.messages.downloadStarted'));
                return;
            }
            // EXCEED_MAX_LIMIT 的上限值拼在错误码里（EXCEED_MAX_LIMIT:50000），需拆出来填进文案
            const rawErrorCode = result?.errorCode || 'DOWNLOAD_FAILED';
            const isExceedLimit = rawErrorCode.startsWith('EXCEED_MAX_LIMIT:');
            const errorCode = isExceedLimit ? 'EXCEED_MAX_LIMIT' : rawErrorCode;
            message.error(
                t(`podRiskReview.errors.${errorCode}`, {
                    maxLimit: isExceedLimit
                        ? Number(rawErrorCode.split(':')[1]).toLocaleString()
                        : undefined,
                    defaultValue: t('podRiskReview.errors.DOWNLOAD_FAILED')
                })
            );
        } catch (error) {
            showError(error);
        }
    };

    const handleBatchReview = () => {
        if (selectedRows.length === 0) {
            message.warning(t('podRiskReview.messages.selectAtLeastOne'));
            return;
        }
        setReviewIds(selectedRows.map(row => row.reviewTaskId));
        setDrawerVisible(true);
    };

    // 单条审完即刷新，不等 Drawer 关闭：reload 刷新当前 Tab（即列表 totalElements），
    // refetchCounts 刷新另一个 Tab
    const handleReviewed = () => {
        reload(filters, currentPage, pageSize, activeTab);
        refetchCounts();
    };

    // Tab 角标：计数加载中显示 '...'，避免闪 0 造成「真的没有数据」的误读
    const getTabLabel = (labelKey: string, count?: number, isLoading?: boolean) =>
        `${t(labelKey)} (${isLoading || count == null ? '...' : count})`;

    /**
     * 当前 Tab 用列表的 totalElements，非当前 Tab 用独立计数查询。
     * 当前 Tab 必须以列表 loading 为准：切 Tab 后新列表返回前 totalElements 仍是旧值，
     * 直接显示会短暂错位。
     */
    const getCountProps = (tab: PodRiskReviewTabType): [number | undefined, boolean] =>
        tab === activeTab
            ? [totalElements, loading]
            : [inactiveCountData?.getPodRiskReviewCount, inactiveCountLoading];

    const tabs = [
        {
            key: PodRiskReviewTabType.NEW,
            label: getTabLabel(
                'podRiskReview.tab.new',
                ...getCountProps(PodRiskReviewTabType.NEW)
            )
        },
        {
            key: PodRiskReviewTabType.TERMINATED,
            label: getTabLabel(
                'podRiskReview.tab.terminated',
                ...getCountProps(PodRiskReviewTabType.TERMINATED)
            )
        }
    ];

    return (
        <div className="p-4">
            <PodRiskReviewFilter
                filters={tempFilters}
                onChange={setTempFilters}
                riskTypeOptions={riskTypeOptions}
                dspOptions={dspSelectOptions}
            />

            <div className="flex justify-between items-center mb-3">
                <Tabs
                    activeKey={activeTab}
                    onChange={key => setActiveTab(key as PodRiskReviewTabType)}
                    items={tabs.map(tab => ({key: tab.key, label: tab.label}))}
                    className="[&_.ant-tabs-nav]:!mb-0 [&_.ant-tabs-tab]:font-bold"
                />
                <Space>
                    {POD_RISK_REVIEW_ACTION_ENABLED &&
                        activeTab === PodRiskReviewTabType.NEW && (
                            <Button
                                type="primary"
                                onClick={handleBatchReview}
                                disabled={selectedRows.length === 0}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">
                                    {t('podRiskReview.action.batchReview', {
                                        count: selectedRows.length
                                    })}
                                </span>
                            </Button>
                        )}
                    {POD_RISK_REVIEW_DOWNLOAD_ENABLED && (
                        <Button
                            type="primary"
                            loading={downloadLoading}
                            onClick={handleDownload}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">
                                {t('podRiskReview.action.download')}
                            </span>
                        </Button>
                    )}
                    <Button
                        type="primary"
                        loading={loading || riskTypesLoading || dspLoading}
                        onClick={handleSearch}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('podRiskReview.action.filter')}</span>
                    </Button>
                    <Button onClick={handleReset} className="w-auto min-w-24">
                        <span className="font-bold">{t('common.reset')}</span>
                    </Button>
                </Space>
            </div>

            <div className="mb-4">
                <PodRiskReviewTable
                    podRiskReviewData={podRiskReviewData}
                    loading={loading}
                    showDetail={showDetail}
                    onSelectChange={(keys, rows) => {
                        setSelectedRowKeys(keys);
                        setSelectedRows(rows);
                    }}
                    selectedRowKeys={selectedRowKeys}
                    dspNameById={dspNameById}
                    showRowSelection={
                        POD_RISK_REVIEW_ACTION_ENABLED &&
                        activeTab === PodRiskReviewTabType.NEW
                    }
                />
            </div>

            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
            />

            <PodRiskReviewDetailDrawer
                open={drawerVisible}
                onClose={() => {
                    // 关闭时清 reviewIds 与选中态并刷新列表，
                    // 否则列表行仍处选中态，再点「批量审核」会带出已审过的最后一条
                    setDrawerVisible(false);
                    setReviewIds([]);
                    // reload 内含 clearSelection（清 selectedRowKeys/selectedRows）+ executeQuery
                    reload(filters, currentPage, pageSize, activeTab);
                    refetchCounts();
                }}
                reviewIds={reviewIds}
                onReviewed={handleReviewed}
                riskTypeOptions={riskTypeOptions}
                riskTypeTextByCode={riskTypeTextByCode}
                dspNameById={dspNameById}
            />
        </div>
    );
};

export default PodRiskReviewPage;
